/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 *
 * @file assignmentDueDateCalendarStyles.js
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import mixins from '../../../assets/styles/mixins.scss';

const assignmentDueDateCalendarShadow = '0px 1px 14px 0px rgba(13, 0, 77, 0.05), '
  + '0px 5px 8px 0px rgba(13, 0, 77, 0.1), '
  + '0px 3px 5px -1px rgba(13, 0, 77, 0.15)';

export const getAssignmentDueDateCalendarStyles = ({ withShadow = false, forAssignmentForm = false } = {}) => ({
  '& .react-datepicker': {
    width: forAssignmentForm ? 450 : '100%',
    border: `1px solid ${mixins.amethystColorV2}`,
    borderRadius: 4,
    boxShadow: withShadow ? assignmentDueDateCalendarShadow : 'none',
    padding: 0,
    overflow: 'hidden',
    fontFamily: mixins.fontFamilyPlusSans,
    backgroundColor: mixins.chalkWhiteFont
  },
  '& .react-datepicker__header': {
    backgroundColor: mixins.chalkWhiteFont,
    borderBottom: 'none',
    padding: 0
  },
  '& .react-datepicker__month': {
    margin: 0,
    padding: 16
  },
  '& .react-datepicker__day-names': {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: 0,
    padding: '16px 16px 0 16px',
    gap: 8
  },
  '& .react-datepicker__day-name': {
    color: '#2E2E2E',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    width: 32,
    margin: 0
  },
  '& .react-datepicker__week': {
    display: 'flex',
    justifyContent: 'space-around',
    gap: 8,
    marginBottom: 8,
    '&:last-child': {
      marginBottom: 0
    }
  },
  '& .react-datepicker__day': {
    color: '#2E2E2E',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    width: 32,
    height: 32,
    lineHeight: '32px',
    margin: 0,
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: mixins.feedBackColor
    }
  },
  '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected': {
    backgroundColor: `${mixins.amethyst} !important`,
    color: `${mixins.chalkWhiteFont} !important`,
    fontWeight: mixins.fontWeight400
  },
  '& .react-datepicker__day--today': {
    fontWeight: mixins.fontWeight400,
    color: '#2E2E2E',
    backgroundColor: mixins.quaternary
  },
  '& .react-datepicker__day--today.react-datepicker__day--selected': {
    backgroundColor: `${mixins.amethyst} !important`,
    color: `${mixins.chalkWhiteFont} !important`
  },
  '& .react-datepicker__day--outside-month': {
    color: '#8B8B8B'
  },
  '& .react-datepicker__day--disabled': {
    color: '#8B8B8B',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  '& .react-datepicker__day--today.react-datepicker__day--disabled': {
    color: '#8B8B8B',
    backgroundColor: 'transparent'
  },
  '& .react-datepicker__day--keyboard-selected.react-datepicker__day--disabled': {
    backgroundColor: 'transparent !important',
    color: '#8B8B8B !important'
  },
  '& .react-datepicker__month-container': {
    width: '100%'
  },
  '& .react-datepicker__triangle': {
    display: 'none'
  }
});

export const getAssignmentDueDateCalendarHeaderStyles = () => ({
  customHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${mixins.amethyst}`
  },
  navGroup: {
    display: 'flex',
    gap: 12,
    alignItems: 'center'
  },
  navButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    minWidth: 18,
    color: mixins.amethyst,
    display: 'flex',
    alignItems: 'center',
    '&:disabled': {
      color: '#E0E0E0',
      cursor: 'not-allowed'
    },
    '& svg': {
      fontSize: 18,
      width: 18,
      height: 18
    }
  },
  monthYearLabel: {
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight600,
    color: '#2E2E2E',
    fontFamily: mixins.fontFamilyPlusSans,
    textAlign: 'center',
    lineHeight: '28.8px'
  }
});
